import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Brain, Instagram, X, Linkedin, Facebook } from 'lucide-react';

// Custom X icon component to better match X (formerly Twitter) branding
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export const Footer: React.FC = () => {
  return (
    <footer className="relative bg-gradient-to-br from-gray-100 via-blue-50 to-slate-100 dark:from-gray-900/95 dark:via-blue-900/95 dark:to-slate-900/95 backdrop-blur-xl border-t border-gray-200 dark:border-blue-500/20 px-4 sm:px-6 lg:px-8">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/5 dark:bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-blue-400/5 dark:bg-blue-400/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto py-6 sm:py-8 lg:py-12">
        
        {/* Mobile Layout (Vertical Stack) */}
        <div className="block sm:hidden space-y-8">
          {/* Mobile Company Info */}
          <div className="text-center">
            {/* Logo & Brand */}
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img 
                src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/3c9c5ef8-4fb8-4601-7c68-b81fc7e6a000/public"
                alt="Cognitive Craft Hub Logo"
                className="w-12 h-12 rounded-xl shadow-lg"
              />
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent block leading-tight">
                  Cognitive Craft Hub
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  AI & Digital Solutions
                </span>
              </div>
            </div>
            
            {/* Mobile Tagline */}
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
              Crafting intelligent digital solutions that empower businesses to thrive in the AI-driven future.
            </p>
          </div>

          {/* Mobile Contact Info */}
          <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white text-center mb-4">Get In Touch</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-center">
                <Phone className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <a href="tel:+15172025360" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm">
                  +1 (517) 202-5360
                </a>
              </div>
              <div className="flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <a href="mailto:team@cognitivecrafthub.com" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm">
                  team@cognitivecrafthub.com
                </a>
              </div>
              <div className="flex items-center justify-center">
                <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">
                  Michigan & Texas
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Quick Links */}
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white text-center mb-4">Quick Links</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-w-sm mx-auto">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">Home</Link>
              <Link to="/about" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">About</Link>
              <Link to="/services" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">Services</Link>
              <Link to="/blog" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">Blog</Link>
              <Link to="/careers" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">Careers</Link>
              <Link to="/investors" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">Investors</Link>
              <Link to="/faq" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">FAQ</Link>
              <Link to="/contact" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm text-center">Contact</Link>
            </div>
          </div>

          {/* Mobile Copyright */}
          <div className="border-t border-gray-300 dark:border-white/10 pt-4 text-center">
            <div className="text-gray-700 dark:text-gray-300 text-xs mb-3">
              &copy; {new Date().getFullYear()} Cognitive Craft Hub. All rights reserved.
            </div>
            
            {/* Mobile Social Media Links */}
            <div className="flex justify-center space-x-4 mb-3">
              <a 
                href="https://www.facebook.com/cognitivecrafthub/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="Like us on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/cognitivecrafthub/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://x.com/CognitiveCraftH" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="Follow us on X"
              >
                <XIcon className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/cognitivecrafthub" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="Connect with us on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
            
            <div className="flex justify-center space-x-4">
              <Link to="/privacy" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-xs">Privacy</Link>
              <Link to="/terms" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-xs">Terms</Link>
            </div>
          </div>
        </div>

        {/* Desktop Layout (Horizontal Grid) */}
        <div className="hidden sm:block">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Desktop Company Info */}
            <div className="col-span-2 lg:col-span-2">
              {/* Logo & Brand */}
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/3c9c5ef8-4fb8-4601-7c68-b81fc7e6a000/public"
                  alt="Cognitive Craft Hub Logo"
                  className="w-16 h-16 rounded-xl shadow-lg"
                />
                <div>
                  <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent block leading-tight">
                    Cognitive Craft Hub
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    AI & Digital Solutions
                  </span>
                </div>
              </div>
              
              {/* Desktop Tagline */}
              <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed mb-6 max-w-md">
                Crafting intelligent digital solutions that empower businesses to thrive in the AI-driven future.
              </p>

              {/* Desktop Contact Information */}
              <div className="space-y-4">
                {/* Locations */}
                <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                      <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Our Locations</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4 ml-9">
                    <div className="text-gray-700 dark:text-gray-300 text-sm">
                      <div className="font-medium text-blue-600 dark:text-blue-400">Michigan</div>
                      <div>East Lansing</div>
                    </div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">
                      <div className="font-medium text-blue-600 dark:text-blue-400">Texas</div>
                      <div>Houston</div>
                    </div>
                  </div>
                </div>
                
                {/* Contact Details */}
                <div className="space-y-3">
                  <div className="flex items-center group">
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                      <Phone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <a href="tel:+15172025360" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium group-hover:underline">
                      +1 (517) 202-5360
                    </a>
                  </div>
                  
                  <div className="flex items-center group">
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                      <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <a href="mailto:team@cognitivecrafthub.com" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium group-hover:underline">
                      team@cognitivecrafthub.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Quick Links */}
            <div>
              <h3 className="text-gray-900 dark:text-white font-semibold mb-4 text-lg">Quick Links</h3>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">About Us</Link></li>
                <li><Link to="/services" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Services</Link></li>
                <li><Link to="/blog" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Blog</Link></li>
                <li><Link to="/careers" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Careers</Link></li>
                <li><Link to="/investors" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Investors</Link></li>
                <li><Link to="/contact" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Desktop Core Services */}
            <div>
              <h3 className="text-gray-900 dark:text-white font-semibold mb-4 text-lg">Core Services</h3>
              <ul className="space-y-3">
                <li><Link to="/services/cognitive-reach" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">AI Marketing</Link></li>
                <li><Link to="/services/cognitive-build" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Custom Software</Link></li>
                <li><Link to="/services/cognitive-intelligence" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">AI Analytics</Link></li>
                <li><Link to="/services/cognitive-cloud" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Cloud Solutions</Link></li>
                <li><Link to="/services/cognitive-secure" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Cybersecurity</Link></li>
                <li><Link to="/services/cognitive-agents" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">AI Automation</Link></li>
                <li><Link to="/services/cognitive-train" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">AI Training</Link></li>
                <li><Link to="/services" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium">View All Services →</Link></li>
              </ul>
            </div>

          </div>

          {/* Desktop Bottom Bar */}
          <div className="border-t border-gray-300 dark:border-white/10 mt-8 pt-6">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
              <div className="text-gray-700 dark:text-gray-300 text-sm">
                &copy; {new Date().getFullYear()} Cognitive Craft Hub. All rights reserved.
              </div>
              
              {/* Social Media Links */}
              <div className="flex items-center space-x-4">
                <a 
                  href="https://www.facebook.com/cognitivecrafthub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  aria-label="Like us on Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.instagram.com/cognitivecrafthub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="https://x.com/CognitiveCraftH" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  aria-label="Follow us on X"
                >
                  <XIcon className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/company/cognitivecrafthub" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  aria-label="Connect with us on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
              
              <div className="flex space-x-6">
                <Link to="/privacy" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm">Privacy Policy</Link>
                <Link to="/terms" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm">Terms & Conditions</Link>
                <Link to="/faq" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm">FAQ</Link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};